import { api } from "@/lib/api";

const base = "/api/accounting";

export type AccountingVoucherStatus =
  | "DRAFT"
  | "SUBMITTED"
  | "APPROVED"
  | "POSTED"
  | "REVERSED"
  | "REJECTED"
  | "CANCELLED";

export type AccountingVoucherType =
  | "JOURNAL"
  | "RECEIPT"
  | "PAYMENT"
  | "CONTRA"
  | "SALES"
  | "PURCHASE"
  | "CREDIT_NOTE"
  | "DEBIT_NOTE"
  | "REFUND"
  | "EXPENSE"
  | "OPENING_BALANCE"
  | "STOCK_ADJUSTMENT"
  | "TRANSFER"
  | "PAYROLL"
  | "TAX_ADJUSTMENT"
  | "TDS"
  | "GST_ADJUSTMENT"
  | "INTER_HOSPITAL"
  | "DEPRECIATION";

export type LedgerAccount = {
  id: string;
  code: string;
  name: string;
  accountType: string;
  normalBalance: string;
  isSystem: boolean;
  isPostable: boolean;
  active: boolean;
  ledgerGroup?: { id: string; code: string; name: string } | null;
};

export type VoucherLine = {
  id: string;
  lineNo: number;
  ledgerAccountId: string;
  debit: string | number;
  credit: string | number;
  narration?: string | null;
  ledgerAccount?: LedgerAccount;
};

export type AccountingVoucher = {
  id: string;
  hospitalId: string;
  voucherNumber: string;
  voucherType: AccountingVoucherType;
  voucherDate: string;
  status: AccountingVoucherStatus;
  narration?: string | null;
  totalDebit: string | number;
  totalCredit: string | number;
  lines?: VoucherLine[];
  financialYear?: { id: string; label: string; startYear: number };
  accountingPeriod?: { id: string; periodKey: string; label: string; status: string };
};

export type AccountingPeriod = {
  id: string;
  periodKey: string;
  label: string;
  startDate: string;
  endDate: string;
  status: "OPEN" | "LOCKED" | "CLOSED";
};

export type FinancialYear = {
  id: string;
  startYear: number;
  label: string;
  status: string;
  periods: AccountingPeriod[];
};

function hospitalParams(hospitalId?: string | null) {
  return hospitalId ? { hospitalId } : undefined;
}

function unwrap<T>(res: { data?: { data?: T; message?: string } }): T {
  return res.data?.data as T;
}

export const accountingApi = {
  getDashboard: (hospitalId?: string | null) =>
    api.get(`${base}/dashboard`, { params: hospitalParams(hospitalId) }).then(unwrap),

  ensureFy: (hospitalId?: string | null, startYear?: number) =>
    api
      .post(`${base}/setup/ensure-fy`, { startYear }, { params: hospitalParams(hospitalId) })
      .then(unwrap),

  seedCoa: (hospitalId?: string | null) =>
    api.post(`${base}/setup/seed-coa`, {}, { params: hospitalParams(hospitalId) }).then(unwrap),

  listFinancialYears: (hospitalId?: string | null) =>
    api.get(`${base}/financial-years`, { params: hospitalParams(hospitalId) }).then(unwrap) as Promise<
      FinancialYear[]
    >,

  listLedgerAccounts: (hospitalId?: string | null, activeOnly = true) =>
    api
      .get(`${base}/ledger-accounts`, {
        params: { ...hospitalParams(hospitalId), activeOnly },
      })
      .then(unwrap) as Promise<LedgerAccount[]>,

  lockPeriod: (periodId: string, hospitalId?: string | null, reason?: string) =>
    api
      .post(
        `${base}/periods/${periodId}/lock`,
        { reason },
        { params: hospitalParams(hospitalId) }
      )
      .then(unwrap),

  reopenPeriod: (periodId: string, hospitalId?: string | null, reason?: string) =>
    api
      .post(
        `${base}/periods/${periodId}/reopen`,
        { reason },
        { params: hospitalParams(hospitalId) }
      )
      .then(unwrap),

  listVouchers: (
    hospitalId?: string | null,
    opts?: { status?: AccountingVoucherStatus; take?: number; skip?: number }
  ) =>
    api
      .get(`${base}/vouchers`, {
        params: { ...hospitalParams(hospitalId), ...opts },
      })
      .then(unwrap) as Promise<AccountingVoucher[]>,

  getVoucher: (id: string, hospitalId?: string | null) =>
    api
      .get(`${base}/vouchers/${id}`, { params: hospitalParams(hospitalId) })
      .then(unwrap) as Promise<AccountingVoucher>,

  createVoucher: (
    body: {
      voucherDate: string;
      voucherType?: AccountingVoucherType;
      narration?: string;
      referenceNumber?: string;
      lines: Array<{
        ledgerAccountId: string;
        debit?: number;
        credit?: number;
        narration?: string;
      }>;
    },
    hospitalId?: string | null
  ) =>
    api
      .post(`${base}/vouchers`, body, { params: hospitalParams(hospitalId) })
      .then(unwrap) as Promise<AccountingVoucher>,

  postVoucher: (id: string, hospitalId?: string | null) =>
    api
      .post(`${base}/vouchers/${id}/post`, {}, { params: hospitalParams(hospitalId) })
      .then(unwrap) as Promise<AccountingVoucher>,

  reverseVoucher: (id: string, hospitalId?: string | null, reason?: string) =>
    api
      .post(
        `${base}/vouchers/${id}/reverse`,
        { reason },
        { params: hospitalParams(hospitalId) }
      )
      .then(unwrap),

  cancelVoucher: (id: string, hospitalId?: string | null) =>
    api
      .post(`${base}/vouchers/${id}/cancel`, {}, { params: hospitalParams(hospitalId) })
      .then(unwrap),

  trialBalance: (
    hospitalId?: string | null,
    opts?: { financialYearId?: string; accountingPeriodId?: string }
  ) =>
    api
      .get(`${base}/reports/trial-balance`, {
        params: { ...hospitalParams(hospitalId), ...opts },
      })
      .then(unwrap) as Promise<{
      rows: Array<{
        ledgerAccountId: string;
        code: string;
        name: string;
        accountType: string;
        debit: number;
        credit: number;
      }>;
      totalDebit: number;
      totalCredit: number;
      balanced: boolean;
    }>,

  generalLedger: (
    hospitalId: string | null | undefined,
    opts: { ledgerAccountId: string; fromDate: string; toDate: string }
  ) =>
    api
      .get(`${base}/reports/general-ledger`, {
        params: { ...hospitalParams(hospitalId), ...opts },
      })
      .then(unwrap),

  openingBalances: (
    body: {
      voucherDate: string;
      narration?: string;
      postImmediately?: boolean;
      lines: Array<{ ledgerAccountId: string; debit?: number; credit?: number; narration?: string }>;
    },
    hospitalId?: string | null
  ) =>
    api
      .post(`${base}/opening-balances`, body, { params: hospitalParams(hospitalId) })
      .then(unwrap),

  opsReconciliation: (
    hospitalId?: string | null,
    opts?: { fromDate?: string; toDate?: string }
  ) =>
    api
      .get(`${base}/reports/ops-reconciliation`, {
        params: { ...hospitalParams(hospitalId), ...opts },
      })
      .then(unwrap) as Promise<{
      note?: string;
      lines: Array<{
        label: string;
        ops: number;
        books: number;
        difference: number;
        eventType: string;
        voucherCount: number;
      }>;
      totals: { ops: number; books: number; difference: number };
    }>,

  subledgerAgeing: (
    kind: "AR" | "AP" | "DOCTOR" | "ADVANCES",
    hospitalId?: string | null,
    asOf?: string
  ) => {
    const path =
      kind === "AR"
        ? "ar-ageing"
        : kind === "AP"
          ? "ap-ageing"
          : kind === "DOCTOR"
            ? "doctor-payables-ageing"
            : "patient-advances-ageing";
    return api
      .get(`${base}/reports/${path}`, {
        params: { ...hospitalParams(hospitalId), asOf },
      })
      .then(unwrap) as Promise<SubledgerAgeingReport>;
  },

  seedTaxRules: (hospitalId?: string | null) =>
    api
      .post(`${base}/setup/seed-tax-rules`, {}, { params: hospitalParams(hospitalId) })
      .then(unwrap),

  gstSummary: (
    hospitalId?: string | null,
    opts?: { fromDate?: string; toDate?: string }
  ) =>
    api
      .get(`${base}/reports/gst-summary`, {
        params: { ...hospitalParams(hospitalId), ...opts },
      })
      .then(unwrap) as Promise<{
      note?: string;
      gstin?: string | null;
      summary: {
        outputGst: number;
        inputGst: number;
        netPayable: number;
        lineCount: number;
      };
      rows: Array<{
        voucherDate: string;
        voucherNumber: string;
        reference?: string | null;
        gstRate: number;
        gstAmount: number;
        cgst: number;
        sgst: number;
      }>;
    }>,

  tdsSummary: (
    hospitalId?: string | null,
    opts?: { fromDate?: string; toDate?: string }
  ) =>
    api
      .get(`${base}/reports/tds-summary`, {
        params: { ...hospitalParams(hospitalId), ...opts },
      })
      .then(unwrap) as Promise<{
      note?: string;
      summary: { withheld: number; remitted: number; payable: number; lineCount: number };
      activeRules: Array<{
        ruleCode: string;
        natureOfPayment: string;
        oldSectionReference?: string | null;
        currentSectionReference?: string | null;
        ratePercent: number;
        thresholdPerTxn: number;
        scopeKey: string;
      }>;
      rows: Array<{
        voucherDate: string;
        voucherNumber: string;
        eventType?: string | null;
        withheld: number;
        remitted: number;
      }>;
    }>,

  downloadTaxCsv: async (
    kind: "gst" | "tds",
    hospitalId?: string | null,
    opts?: { fromDate?: string; toDate?: string }
  ) => {
    const path = kind === "gst" ? "gst-summary" : "tds-summary";
    const res = await api.get(`${base}/reports/${path}`, {
      params: { ...hospitalParams(hospitalId), ...opts, format: "csv" },
      responseType: "blob",
    });
    const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${kind}-books.csv`;
    a.click();
    URL.revokeObjectURL(url);
  },

  listBankAccounts: (hospitalId?: string | null) =>
    api
      .get(`${base}/bank-accounts`, { params: hospitalParams(hospitalId) })
      .then(unwrap) as Promise<
      Array<{
        id: string;
        name: string;
        bankName?: string | null;
        accountNumber?: string | null;
        ifsc?: string | null;
        active: boolean;
        ledgerAccount?: { id: string; code: string; name: string } | null;
      }>
    >,

  createBankAccount: (
    body: {
      name: string;
      bankName?: string;
      accountNumber?: string;
      ifsc?: string;
      notes?: string;
    },
    hospitalId?: string | null
  ) =>
    api
      .post(`${base}/bank-accounts`, body, { params: hospitalParams(hospitalId) })
      .then(unwrap),

  importBankStatement: (
    body: {
      bankAccountId: string;
      statementDate: string;
      openingBalance?: number;
      closingBalance?: number;
      sourceFilename?: string;
      csv?: string;
      lines?: Array<{
        txnDate: string;
        description?: string;
        reference?: string;
        debit?: number;
        credit?: number;
      }>;
      autoMatch?: boolean;
    },
    hospitalId?: string | null
  ) =>
    api
      .post(`${base}/bank/statements/import`, body, { params: hospitalParams(hospitalId) })
      .then(unwrap),

  listBankStatementLines: (
    hospitalId?: string | null,
    opts?: { bankAccountId?: string; status?: string; fromDate?: string; toDate?: string }
  ) =>
    api
      .get(`${base}/bank/statement-lines`, {
        params: { ...hospitalParams(hospitalId), ...opts },
      })
      .then(unwrap) as Promise<
      Array<{
        id: string;
        txnDate: string;
        description?: string | null;
        reference?: string | null;
        debit: string | number;
        credit: string | number;
        status: string;
        matchedVoucherId?: string | null;
        matchNote?: string | null;
        bankAccount?: { id: string; name: string };
      }>
    >,

  ignoreBankStatementLine: (id: string, hospitalId?: string | null, note?: string) =>
    api
      .post(
        `${base}/bank/statement-lines/${id}/ignore`,
        { note },
        { params: hospitalParams(hospitalId) }
      )
      .then(unwrap),

  unmatchBankStatementLine: (id: string, hospitalId?: string | null) =>
    api
      .post(
        `${base}/bank/statement-lines/${id}/unmatch`,
        {},
        { params: hospitalParams(hospitalId) }
      )
      .then(unwrap),

  autoMatchBank: (bankAccountId: string, hospitalId?: string | null) =>
    api
      .post(
        `${base}/bank/auto-match`,
        { bankAccountId },
        { params: hospitalParams(hospitalId) }
      )
      .then(unwrap) as Promise<{ matched: number }>,

  bankReconStatus: (hospitalId?: string | null, bankAccountId?: string) =>
    api
      .get(`${base}/reports/bank-reconciliation`, {
        params: { ...hospitalParams(hospitalId), bankAccountId },
      })
      .then(unwrap) as Promise<{
      note?: string;
      counts: { unmatched: number; matched: number; ignored: number };
      unmatchedDebit: number;
      unmatchedCredit: number;
      accounts: Array<{
        id: string;
        name: string;
        bankName?: string | null;
        active: boolean;
        ledgerCode?: string | null;
      }>;
    }>,

  pharmacyInventorySummary: (
    hospitalId?: string | null,
    opts?: { fromDate?: string; toDate?: string }
  ) =>
    api
      .get(`${base}/reports/pharmacy-inventory`, {
        params: { ...hospitalParams(hospitalId), ...opts },
      })
      .then(unwrap) as Promise<{
      note?: string;
      purchases: number;
      cogs: number;
      closingBooks: number;
      lineCount: number;
      inventoryAccount?: { id: string; code: string; name: string } | null;
    }>,

  profitAndLoss: (
    hospitalId: string | null | undefined,
    opts: { fromDate: string; toDate: string }
  ) =>
    api
      .get(`${base}/reports/profit-loss`, {
        params: { ...hospitalParams(hospitalId), ...opts },
      })
      .then(unwrap) as Promise<{
      note?: string;
      fromDate: string;
      toDate: string;
      sections: {
        revenue: Array<{ code: string; name: string; balance: number }>;
        directCosts: Array<{ code: string; name: string; balance: number }>;
        expenses: Array<{ code: string; name: string; balance: number }>;
      };
      totals: {
        totalRevenue: number;
        totalDirectCosts: number;
        grossProfit: number;
        totalExpenses: number;
        netProfit: number;
      };
    }>,

  balanceSheet: (hospitalId: string | null | undefined, asOf: string) =>
    api
      .get(`${base}/reports/balance-sheet`, {
        params: { ...hospitalParams(hospitalId), asOf },
      })
      .then(unwrap) as Promise<{
      note?: string;
      asOf: string;
      assets: Array<{ code: string; name: string; balance: number }>;
      liabilities: Array<{ code: string; name: string; balance: number }>;
      equity: Array<{ code: string; name: string; balance: number }>;
      totals: {
        totalAssets: number;
        totalLiabilities: number;
        totalEquity: number;
        currentYearSurplus: number;
        totalLiabEquity: number;
        difference: number;
        balanced: boolean;
      };
    }>,

  cashFlow: (
    hospitalId: string | null | undefined,
    opts: { fromDate: string; toDate: string }
  ) =>
    api
      .get(`${base}/reports/cash-flow`, {
        params: { ...hospitalParams(hospitalId), ...opts },
      })
      .then(unwrap) as Promise<{
      note?: string;
      lines: Array<{ key: string; label: string; amount: number }>;
      totals: {
        cashFromOperations: number;
        cashFromInvesting: number;
        cashFromFinancing: number;
        netChangeInCash: number;
        openingCash: number;
        closingCash: number;
        impliedClosing: number;
        reconDifference: number;
        reconciles: boolean;
      };
    }>,

  fixedAssetRegister: (hospitalId?: string | null) =>
    api
      .get(`${base}/fixed-assets/register`, { params: hospitalParams(hospitalId) })
      .then(unwrap) as Promise<{
      rows: Array<{
        id: string;
        assetCode?: string | null;
        name: string;
        category: string;
        status: string;
        cost: number;
        accumDep: number;
        netBookValue: number;
        usefulLifeMonths: number;
      }>;
      totals: { cost: number; accumDep: number; netBookValue: number; count: number };
    }>,

  createFixedAsset: (
    body: {
      name: string;
      assetCode?: string;
      category?: string;
      purchaseDate: string;
      putToUseDate?: string;
      cost: number;
      salvageValue?: number;
      usefulLifeMonths: number;
      fundedBy?: "CASH" | "BANK" | "AP" | "CAPITAL";
      notes?: string;
    },
    hospitalId?: string | null
  ) =>
    api
      .post(`${base}/fixed-assets`, body, { params: hospitalParams(hospitalId) })
      .then(unwrap),

  runDepreciation: (periodKey: string, hospitalId?: string | null) =>
    api
      .post(
        `${base}/fixed-assets/depreciation/run`,
        { periodKey },
        { params: hospitalParams(hospitalId) }
      )
      .then(unwrap) as Promise<{
      periodKey: string;
      postedCount: number;
      totalDepreciation: number;
      results: Array<{ assetId: string; name: string; amount: number; status: string }>;
    }>,

  booksKpis: (
    hospitalId: string | null | undefined,
    opts: { fromDate: string; toDate: string }
  ) =>
    api
      .get(`${base}/reports/books-kpis`, {
        params: { ...hospitalParams(hospitalId), ...opts },
      })
      .then(unwrap) as Promise<{
      note?: string;
      fromDate: string;
      toDate: string;
      kpis: Array<{ key: string; label: string; unit: string; value: number }>;
      coverage: {
        hospitalsWithBooks: number;
        hospitalsTotal: number;
        postedVoucherCount: number;
        readyForIntelligence: boolean;
      };
      byHospital: Array<{
        hospitalId: string;
        hospitalName: string;
        postedVoucherCount: number;
        hasBooks: boolean;
      }>;
    }>,
};

export type AgeingBucketKey =
  | "b0_30"
  | "b31_60"
  | "b61_90"
  | "b91_180"
  | "b180_plus";

export type SubledgerAgeingReport = {
  kind: string;
  asOf: string;
  note?: string;
  bucketDefs: Array<{ key: AgeingBucketKey; label: string }>;
  controlAccounts?: Array<{ code: string; name: string }>;
  rows: Array<{
    partyId: string;
    partyName: string;
    partyCode?: string | null;
    partyType: string;
    accountCodes: string[];
    total: number;
    buckets: Record<AgeingBucketKey, number>;
    creditBalance: number;
    openItemCount: number;
  }>;
  totals: {
    total: number;
    creditBalance: number;
    buckets: Record<AgeingBucketKey, number>;
    partyCount: number;
  };
};

export function moneyNum(v: string | number | null | undefined): number {
  if (v == null || v === "") return 0;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
}

export function formatInr(n: number) {
  return `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
